import { useTranslation } from 'react-i18next';

import { formatNumber } from './format';

/**
 * The unit a figure carries, as a **declared property of the metric** rather than a branch on its key.
 * `null` is a bare count: *Submitted this period* is interviews, and *"24 interviews"* would be the
 * label said twice.
 */
export type Unit = 'hours' | 'percent' | null;

/**
 * A number with its unit **glued** for the eye and spelled out for the ear (`DESIGN.md` §6.2, §9).
 *
 * ⚠️ **It lived inside `HealthSection.tsx` until 2026-09-28 and moved here because §6.2 Section 5 needs
 * it too** (BUG-092). *Median turnaround* is the **same quantity** in both sections — hours from
 * `scheduled_at` to `submitted_at` — and Section 5 rendered it bare: `15.5`, with no unit on the figure
 * and none in the column header either, so a reader could not tell days from hours on the one screen
 * that already answers the question two sections higher. Copying the glue would have been a second
 * grammar for one quantity, which is the argument `cardDates.ts` makes for `onDateTime` having two
 * importers rather than two implementations.
 *
 * ⚠️ **The glue is visual, not semantic**: `h` read aloud is a letter, so the glued form is hidden from
 * assistive technology and the words sit beside it. The age in Section 1 takes the same treatment.
 *
 * ⚠️ **A unitless figure renders once, not twice.** *Submitted this period* is a bare count, so `24` and
 * *"24"* would be the same string in two elements — a screen reader hearing a number twice. Only a
 * figure that gains a letter needs the pair.
 *
 * ⚠️ **The spoken form is a counted string** (`_one` / `_other`, 2026-09-28). It interpolated
 * `{{value}}` into a single *"{{value}} hours"* until then, so a median of exactly `1` was read aloud as
 * *"1 hours"* — the defect that put *"1 sets"* on a live screen in Sprint 20, latent here because
 * nothing had yet rendered a median of one. The plural is selected from `count`, which is the **raw**
 * number; what is interpolated is `value`, the formatted one, so `15.5` keeps its single decimal while
 * English still picks *other* for it.
 *
 * A `null` value is the em dash with nothing glued to it: `—h` would be a unit on a measurement nobody
 * took.
 */
export function Glued({ value, unit }: { value: number | null; unit: Unit }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const noValue = t('common:noValue');

  if (value === null) return <>{noValue}</>;

  const number = formatNumber(value, noValue);
  if (unit === null) return <>{number}</>;

  return (
    <>
      <span aria-hidden>
        {number}
        {t(`dashboard:units.suffix.${unit}`)}
      </span>
      <span className="sr-only">
        {t(`dashboard:units.spoken.${unit}`, { count: value, value: number })}
      </span>
    </>
  );
}
